/**
 * Password generation utility functions
 */

const LOWERCASE = "abcdefghijklmnopqrstuvwxyz";
const UPPERCASE = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const NUMBERS = "0123456789";
const SPECIAL_CHARS = "@$!%*?&";

/**
 * Generates a secure password that meets the validation requirements
 * @param length - Password length (minimum 8, default 12)
 * @returns A randomly generated password
 */
export const generateSecurePassword = (length = 12): string => {
  // Ensure minimum length
  const minLength = Math.max(8, length);

  // Ensure we have at least one character from each required category
  const requiredChars = [
    LOWERCASE[Math.floor(Math.random() * LOWERCASE.length)],
    UPPERCASE[Math.floor(Math.random() * UPPERCASE.length)],
    NUMBERS[Math.floor(Math.random() * NUMBERS.length)],
    SPECIAL_CHARS[Math.floor(Math.random() * SPECIAL_CHARS.length)],
  ];

  // Fill the rest with random characters from all categories
  const allChars = LOWERCASE + UPPERCASE + NUMBERS + SPECIAL_CHARS;
 const remainingChars: string[] = [];

  for (let i = 4; i < minLength; i++) {
    remainingChars.push(allChars[Math.floor(Math.random() * allChars.length)]);
  }

  // Combine and shuffle all characters
  const passwordArray = [...requiredChars, ...remainingChars];

  // Fisher-Yates shuffle algorithm
  for (let i = passwordArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [passwordArray[i], passwordArray[j]] = [passwordArray[j], passwordArray[i]];
  }

  return passwordArray.join("");
};

/**
 * Validates if a password meets the security requirements
 * @param password - Password to validate
 * @returns Object with validation result and details
 */
export const validatePasswordStrength = (password: string) => {
  const checks = {
    length: password.length >= 8,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    number: /\d/.test(password),
    special: /[@$!%*?&]/.test(password),
  };

  const isValid = Object.values(checks).every(Boolean);

  return {
    isValid,
    checks,
    score: Object.values(checks).filter(Boolean).length,
  };
};
